import React, {useEffect, useState} from 'react'
import {FileUploader} from 'react-drag-drop-files'
import {useFormikContext} from 'formik'
import {
  AspectRatio,
  Box,
  CircularProgress,
  CircularProgressLabel,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import {AttachmentIcon, CloseIcon, DownloadIcon, InfoIcon} from '@chakra-ui/icons'
import {useUpload} from '@frnt/base/providers'

const fileTypes = ['JPG', 'PNG', 'PDF', 'JPEG']

const isPdf = (file?: {url?: string; name?: string} | null) => {
  if (!file) return false
  if (file.name?.toLowerCase().endsWith('.pdf')) return true
  try {
    const path = new URL(file.url || '').pathname
    return path.toLowerCase().endsWith('.pdf')
  } catch {
    return (file.url || '').toLowerCase().includes('.pdf')
  }
}

// test
interface FileInputFieldProps {
  name: string
  value?: any
  onChange?: (e: any) => void
  onBlur?: () => void
  isDisabled?: boolean
  hasMultiple?: boolean
  maxSize?: number
}

type status = 'empty' | 'uploading' | 'uploaded' | 'error'

const getUploadedFiles = (value: any): Array<{url: string; name?: string}> => {
  if (Array.isArray(value)) {
    return value.filter((item) => !!item?.url)
  }

  if (value?.url) {
    return [value]
  }

  return []
}

// This an MVP file input field, it should be improved to handle errors and other states
// TODO Adnan: improve this component
export const FileInputField = ({name, value = {}, isDisabled, hasMultiple, onChange, maxSize = 5}: FileInputFieldProps) => {
  const {setFieldValue} = useFormikContext<any>()
  const {uploadUrl, accessToken} = useUpload()
  const toast = useToast()

  const [status, setStatus] = useState<status>('empty')
  const [progress, setProgress] = useState(0)

  const [file, setFile] = useState<any>(null)
  const handleChange = (file: any) => {
    setFile(file)
  }

  useEffect(() => {
    if (getUploadedFiles(value).length > 0) {
      setStatus('uploaded')
      return
    }

    setStatus('empty')
  }, [value])

  const onChangeInternal = (value: any) => {
    if (onChange) {
      // pass an event to the parent component
      const event: any = {
        target: {
          name,
          value
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        persist: () => {}
      }
      onChange(event)
    }
  }

  // Progress simulation effect
  useEffect(() => {
    let progressInterval: NodeJS.Timeout
    if (status === 'uploading' && progress < 99) {
      progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 99))
      }, 200)
    }
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }, [status, progress])

  useEffect(() => {
    if (!file) return
    if (!hasMultiple && getUploadedFiles(value).length > 0) {
      setFile(null)
      return
    }

    const uploadSingleFile = async (doc: File): Promise<{url: string; name: string} | null> => {
      const formData = new FormData()
      formData.append('file', doc, doc.name)

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        return {url: data.url, name: doc.name}
      }
      return null
    }

    const uploadFiles = async (rawDoc: any) => {
      const docs: File[] = []
      if (rawDoc instanceof FileList) {
        for (let i = 0; i < rawDoc.length; i++) docs.push(rawDoc[i])
      } else if (Array.isArray(rawDoc)) {
        docs.push(...rawDoc.filter((d: any) => d instanceof File))
      } else if (rawDoc instanceof File) {
        docs.push(rawDoc)
      }
      if (docs.length === 0) return

      setStatus('uploading')
      setProgress(0)

      try {
        const results = await Promise.all(docs.map(uploadSingleFile))
        const uploaded = results.filter(Boolean) as {url: string; name: string}[]

        if (uploaded.length === 0) {
          setStatus('error')
          return
        }

        if (hasMultiple) {
          const existing = getUploadedFiles(value)
          const nextValue = [...existing, ...uploaded]
          setFieldValue(name, nextValue)
          onChangeInternal(nextValue)
        } else {
          setFieldValue(name, uploaded[0])
          onChangeInternal(uploaded[0])
        }

        setProgress(100)
        setStatus('uploaded')
      } catch (error) {
        setStatus('error')
      } finally {
        setFile(null)
      }
    }
    uploadFiles(file)
  }, [file, value])

  const [isPreviewError, setIsPreviewError] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [hasRetriedPreview, setHasRetriedPreview] = useState(false)
  const uploadedFiles = getUploadedFiles(value)
  const displayFile = uploadedFiles[0]
  const displayFileUrl = previewUrl || displayFile?.url

  const extractStorageKeyFromUrl = (url: string): string | null => {
    if (!url) return null

    try {
      const parsed = new URL(url)
      return decodeURIComponent(parsed.pathname.replace(/^\//, ''))
    } catch {
      return decodeURIComponent(url.split('?')[0].replace(/^https?:\/\/[^/]+\//, ''))
    }
  }

  useEffect(() => {
    setIsPreviewError(false)
    setHasRetriedPreview(false)
    setPreviewUrl(displayFile?.url || null)
  }, [displayFile?.url])

  const uploaderDropZone = (
    <FileUploader
      onSizeError={() => {
        toast({
          title: 'File size is too big',
          description: `The file you've uploaded is too big, please upload a file smaller than ${maxSize}MB`,
          status: 'error',
          duration: 3000
        })
      }}
      onTypeError={() => {
        toast({
          title: 'File type is not supported',
          description: `The file you've uploaded is not supported, please upload a file with the following extensions: ${fileTypes.join(', ')}`,
          status: 'error',
          duration: 3000
        })
      }}
      disabled={isDisabled || (status === 'uploaded' && !hasMultiple)}
      handleChange={handleChange}
      multiple={hasMultiple}
      maxSize={maxSize}
      name={name}
      types={fileTypes}
    >
      <Box
        w="100%"
        rounded={4}
        borderWidth="1px"
        borderColor="gray.200"
        borderStyle={uploadedFiles.length > 0 ? 'dashed' : 'solid'}
        boxShadow="sm"
        overflow="hidden"
        _hover={{borderColor: 'gray.300', boxShadow: 'sm'}}
      >
        {status === 'uploading' ? (
          <Box p={3} bgColor="white">
            <HStack spacing={3}>
              <CircularProgress value={progress} size="50px" thickness="6px" color="primary2.500" trackColor="gray.100">
                <CircularProgressLabel fontSize="xs" fontWeight="bold">{progress}%</CircularProgressLabel>
              </CircularProgress>
              <VStack align="start" spacing={1} flex={1} minW={0}>
                <Skeleton h="14px" w="60%" rounded="sm" />
                <Text fontSize="xs" color="gray.400">Uploading...</Text>
              </VStack>
            </HStack>
          </Box>
        ) : status === 'error' ? (
          <Box textAlign="center" p={3} color="red.500">
            <Text>Upload failed. Please try again.</Text>
          </Box>
        ) : (
          <VStack textAlign="center" p={3} color="gray.400" bgColor="white" cursor={isDisabled ? 'not-allowed' : 'pointer'} spacing={1}>
            <DownloadIcon boxSize={uploadedFiles.length > 0 ? 4 : 6} />
            <Text fontSize={uploadedFiles.length > 0 ? 'xs' : 'sm'}>
              {uploadedFiles.length > 0 ? 'Add more files' : 'Drag & drop a file here or choose a file'}
            </Text>
            <Text fontSize="xs" fontWeight="bold">
              ({fileTypes.join(', ')})
            </Text>
          </VStack>
        )}
      </Box>
    </FileUploader>
  )

  if (!hasMultiple || uploadedFiles.length === 0) {
    return (
      <>
        {!hasMultiple && status === 'uploaded' ? (
          <Box w="100%" rounded={4} borderWidth="1px" borderColor="gray.200" boxShadow="sm" overflow="hidden">
            <Box p={3} color="neutral4" bgColor="white">
              <HStack justifyContent="space-between" spacing={3}>
                <HStack spacing={3} minW={0} flex={1}>
                  {isPdf(displayFile) ? (
                    <HStack
                      w="50px" h="50px" alignItems="center" justifyContent="center"
                      bg="red.50" rounded={4} flexShrink={0}
                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      onClick={() => { if (!isDisabled) window.open(displayFileUrl || displayFile?.url, '_blank') }}
                    >
                      <AttachmentIcon boxSize={5} color="red.400" />
                    </HStack>
                  ) : isPreviewError ? (
                    <HStack w="50px" h="50px" alignItems="center" justifyContent="center">
                      <InfoIcon w="40px" fontSize="xl" />
                    </HStack>
                  ) : (
                    <AspectRatio maxW="100%" w="50px" ratio={1} rounded={4} overflow="hidden">
                      <Image
                        onError={async () => {
                          if (!hasRetriedPreview && displayFile?.url && uploadUrl && accessToken) {
                            setHasRetriedPreview(true)
                            const key = extractStorageKeyFromUrl(displayFile.url)
                            if (key) {
                              try {
                                const response = await fetch(`${uploadUrl}?key=${encodeURIComponent(key)}`, {
                                  headers: {Authorization: `Bearer ${accessToken}`}
                                })
                                if (response.ok) {
                                  const data = await response.json()
                                  if (data?.url) { setPreviewUrl(data.url); return }
                                }
                              } catch { /* fall through */ }
                            }
                          }
                          setIsPreviewError(true)
                        }}
                        onClick={() => { if (!isDisabled) setIsPreviewOpen(true) }}
                        src={displayFileUrl || ''}
                        alt={displayFile?.name}
                        objectFit="contain"
                        cursor={isDisabled ? 'not-allowed' : 'zoom-in'}
                      />
                    </AspectRatio>
                  )}
                  <Text fontSize="sm" noOfLines={1}>{displayFile?.name}</Text>
                </HStack>
                <HStack px={1} py={1} spacing={1}>
                  {!isDisabled && (
                    <IconButton
                      onClick={() => {
                        if (!window.confirm('Are you sure you want to delete this file?')) return
                        handleChange(null)
                        setFieldValue(name, null)
                        onChangeInternal(null)
                        setStatus('empty')
                      }}
                      aria-label="delete"
                      size="xs"
                      icon={<CloseIcon />}
                      variant="ghost"
                    />
                  )}
                  <IconButton
                    onClick={() => window.open(displayFileUrl || displayFile?.url, '_blank')}
                    aria-label="download"
                    size="xs"
                    icon={<DownloadIcon />}
                    variant="ghost"
                  />
                </HStack>
              </HStack>
            </Box>
          </Box>
        ) : (
          uploaderDropZone
        )}
        <Modal isOpen={isPreviewOpen && !isPreviewError && !isPdf(displayFile)} onClose={() => setIsPreviewOpen(false)} isCentered size="full">
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent bg="transparent" boxShadow="none">
            <ModalCloseButton color="white" />
            <ModalBody p={6} display="flex" alignItems="center" justifyContent="center">
              <Image src={displayFileUrl || ''} alt={displayFile?.name} maxW="95vw" maxH="90vh" objectFit="contain" />
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    )
  }

  return (
    <VStack spacing={2} align="stretch" w="100%">
      {uploadedFiles.map((uploadedFile, index) => (
        <Box key={uploadedFile.url} w="100%" rounded={4} borderWidth="1px" borderColor="gray.200" boxShadow="sm" overflow="hidden">
          <Box p={3} color="neutral4" bgColor="white">
            <HStack justifyContent="space-between" spacing={3}>
              <HStack spacing={3} minW={0} flex={1}>
                {isPdf(uploadedFile) ? (
                  <HStack
                    w="50px" h="50px" alignItems="center" justifyContent="center"
                    bg="red.50" rounded={4} flexShrink={0}
                    cursor="pointer"
                    onClick={() => window.open(uploadedFile.url, '_blank')}
                  >
                    <AttachmentIcon boxSize={5} color="red.400" />
                  </HStack>
                ) : (
                  <AspectRatio maxW="100%" w="50px" ratio={1} rounded={4} overflow="hidden">
                    <Image
                      onError={async () => {
                        if (index === 0 && !hasRetriedPreview && uploadedFile?.url && uploadUrl && accessToken) {
                          setHasRetriedPreview(true)
                          const key = extractStorageKeyFromUrl(uploadedFile.url)
                          if (key) {
                            try {
                              const response = await fetch(`${uploadUrl}?key=${encodeURIComponent(key)}`, {
                                headers: {Authorization: `Bearer ${accessToken}`}
                              })
                              if (response.ok) {
                                const data = await response.json()
                                if (data?.url) { setPreviewUrl(data.url); return }
                              }
                            } catch { /* fall through */ }
                          }
                        }
                      }}
                      onClick={() => window.open(index === 0 && previewUrl ? previewUrl : uploadedFile.url, '_blank')}
                      src={(index === 0 && previewUrl ? previewUrl : uploadedFile.url) || ''}
                      alt={uploadedFile?.name}
                      objectFit="contain"
                      cursor="zoom-in"
                    />
                  </AspectRatio>
                )}
                <Text fontSize="sm" noOfLines={1}>{uploadedFile?.name}</Text>
              </HStack>
              <HStack px={1} py={1} spacing={1}>
                {!isDisabled && (
                  <IconButton
                    onClick={() => {
                      if (!window.confirm('Are you sure you want to delete this file?')) return
                      const remaining = uploadedFiles.filter((_, i) => i !== index)
                      setFieldValue(name, remaining)
                      onChangeInternal(remaining)
                      if (remaining.length === 0) setStatus('empty')
                    }}
                    aria-label="delete"
                    size="xs"
                    icon={<CloseIcon />}
                    variant="ghost"
                  />
                )}
                <IconButton
                  onClick={() => window.open(uploadedFile?.url, '_blank')}
                  aria-label="download"
                  size="xs"
                  icon={<DownloadIcon />}
                  variant="ghost"
                />
              </HStack>
            </HStack>
          </Box>
        </Box>
      ))}
      {!isDisabled && uploaderDropZone}
    </VStack>
  )
}
